import type React from 'react'
import { Alert, Button, Checkbox, EmptyState, FieldBlock, Input, LoadingState, Select, Tabs, Textarea, useToast } from '@orbind/ui'
import { useEffect, useMemo, useState } from 'react'
import { FormRow, FormScreen, FormSection } from '../../components/FormScreen.tsx'
import { type Bootstrap, useBootstrap } from '../../lib/catalog.ts'
import { useEtapasIniciais } from '../work-item/etapas-iniciais.ts'
import { useT } from '../../lib/i18n.tsx'
import {
  type ItemChecklistTemplate,
  type TemplateKind,
  type TemplateResumo,
  useExcluirTemplate,
  useSalvarTemplateChecklist,
  useTemplate,
  useTemplates,
} from './template-data.ts'
import { TemplateNoItemPage } from './TemplateNoItemPage.tsx'
import './templates.css'

/**
 * Biblioteca de templates (SPEC-20260830-1225).
 *
 * Duas abas: **Itens de Trabalho** (modelos capturados de um item real, pelo
 * "Salvar como template" do proprio item) e **Checklists** (criados aqui, do
 * zero, com itens que podem virar Itens de Trabalho filhos).
 */

type Rascunho = {
  id: string | null
  name: string
  description: string
  checklistName: string
  items: ItemChecklistTemplate[]
}

const VAZIO: Rascunho = { id: null, name: '', description: '', checklistName: '', items: [{ text: '', criaItem: false }] }

export function TemplatesSection({ criarSinal = 0 }: { criarSinal?: number }) {
  const [aba, setAba] = useState<TemplateKind>('work_item')
  const [rascunho, setRascunho] = useState<Rascunho | null>(null)
  const [editando, setEditando] = useState<TemplateResumo | null>(null)
  /** Criando um template de ITEM do zero (editor de arvore, 2026-08-30). */
  const [criandoItem, setCriandoItem] = useState(false)

  const lista = useTemplates()
  const excluir = useExcluirTemplate()
  const toast = useToast()

  // "+" do explorer: sempre cria um template de CHECKLIST (o de item nasce
  // dentro do proprio Item de Trabalho, pelo "Salvar como template")
  useEffect(() => {
    if (criarSinal > 0) {
      setAba('checklist')
      setRascunho({ ...VAZIO })
    }
  }, [criarSinal])

  const doTipo = useMemo(
    () => (lista.data?.templates ?? []).filter((t) => t.kind === aba),
    [lista.data, aba],
  )

  if (rascunho) return <EditorChecklist rascunho={rascunho} setRascunho={setRascunho} onFechar={() => setRascunho(null)} />
  // template de ITEM abre na MESMA tela do Item de Trabalho (SPEC-20260830-1530)
  if (editando) return <TemplateNoItemPage template={editando} onFechar={() => setEditando(null)} />
  if (criandoItem) return <TemplateNoItemPage template={null} onFechar={() => setCriandoItem(false)} />

  return (
    <div className="tpl">
      <div className="tpl__topo">
        <Tabs
          items={[
            { id: 'work_item', label: 'Itens de Trabalho' },
            { id: 'checklist', label: 'Checklists' },
          ]}
          value={aba}
          onChange={(v) => setAba(v as TemplateKind)}
        />
        <Button
          variant="primary"
          onClick={() => (aba === 'checklist' ? setRascunho({ ...VAZIO }) : setCriandoItem(true))}
        >
          <i className="fas fa-plus" aria-hidden />
          {aba === 'checklist' ? ' Novo template de checklist' : ' Novo template de item'}
        </Button>
      </div>

      <p className="tpl__ajuda">
        {aba === 'work_item'
          ? 'Modelos da cadeia inteira de um Item de Trabalho — filhos, checklists, campos complementares, membros e etiquetas. Crie um aqui do zero, ou abra um item pronto e use “Salvar como template”.'
          : 'Modelos de checklist. Um item da lista pode virar um Item de Trabalho filho quando o template for aplicado.'}
      </p>

      {lista.isLoading ? <LoadingState label="Carregando templates…" /> : null}
      {lista.error ? <Alert tone="danger">Não foi possível carregar os templates.</Alert> : null}

      {!lista.isLoading && doTipo.length === 0 ? (
        <EmptyState
          icon="fas fa-clone"
          title={aba === 'work_item' ? 'Nenhum template de item ainda' : 'Nenhum template de checklist ainda'}
          description={
            aba === 'work_item'
              ? 'Crie um aqui do zero, ou abra um Item de Trabalho bem montado e use “Salvar como template” para reaproveitar a estrutura dele.'
              : 'Crie o primeiro template de checklist para reaproveitar listas que se repetem.'
          }
          action={
            <Button
              variant="primary"
              onClick={() => (aba === 'checklist' ? setRascunho({ ...VAZIO }) : setCriandoItem(true))}
            >
              {aba === 'checklist' ? 'Novo template de checklist' : 'Novo template de item'}
            </Button>
          }
        />
      ) : null}

      {doTipo.length > 0 ? (
        <div className="tpl__lista">
          {doTipo.map((t) => (
            <div key={t.id} className="tpl__card">
              <i className={`tpl__card-ico ${t.kind === 'work_item' ? 'fas fa-sitemap' : 'fas fa-list-check'}`} aria-hidden />
              <div className="tpl__card-txt">
                <button type="button" className="tpl__card-nome" onClick={() => (t.kind === 'checklist' ? abrirChecklist(t, setRascunho) : setEditando(t))}>
                  {t.name}
                </button>
                {t.description ? <p className="tpl__card-desc">{t.description}</p> : null}
              </div>
              {t.kind === 'work_item' ? (
                <span className="tpl__card-tag" title="Como o template nasce ao ser aplicado">
                  {t.config.resetStatus === false ? 'Mantém status' : 'Status inicial'}
                </span>
              ) : null}
              <button
                type="button"
                className="tpl__card-btn"
                title="Editar"
                aria-label={`Editar ${t.name}`}
                onClick={() => (t.kind === 'checklist' ? abrirChecklist(t, setRascunho) : setEditando(t))}
              >
                <i className="fas fa-pen" aria-hidden />
              </button>
              <button
                type="button"
                className="tpl__card-btn tpl__card-btn--perigo"
                title="Excluir"
                aria-label={`Excluir ${t.name}`}
                onClick={() => {
                  if (!confirm(`Excluir o template “${t.name}”? Os itens já criados a partir dele não são afetados.`)) return
                  excluir.mutate(t.id, {
                    onSuccess: () => toast.push({ tone: 'success', message: 'Template excluído.' }),
                  })
                }}
              >
                <i className="fas fa-trash" aria-hidden />
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/** Rotulo do catalogo: traducao pela chave, com o codigo como fallback. */
function rotuloTraduzido(row: { code: string; nameTranslationKey?: string | null }, t: (k: string) => string) {
  const chave = row.nameTranslationKey ?? `x.${row.code}`
  const label = t(chave)
  return label === chave ? row.code : label
}

/** Abre o editor de um template de checklist ja salvo (carrega o payload). */
function abrirChecklist(t: TemplateResumo, setRascunho: (r: Rascunho) => void) {
  setRascunho({ id: t.id, name: t.name, description: t.description, checklistName: '', items: [] })
}

// ========================================================= editor de checklist

function EditorChecklist({
  rascunho,
  setRascunho,
  onFechar,
}: {
  rascunho: Rascunho
  setRascunho: React.Dispatch<React.SetStateAction<Rascunho | null>>
  onFechar: () => void
}) {
  const existente = useTemplate(rascunho.id)
  const salvar = useSalvarTemplateChecklist()
  const bootstrap = useBootstrap()
  const t = useT()
  const toast = useToast()
  const [carregou, setCarregou] = useState(rascunho.id === null)

  // ao abrir um template salvo, hidrata o rascunho com o payload
  useEffect(() => {
    if (carregou || !existente.data) return
    const payload = existente.data.template.payload as {
      name?: string
      items?: {
        text: string
        child: {
          type?: { id: string | null }
          status?: { id: string | null }
          priority?: { id: string | null }
          flowStep?: { id: string | null }
          companyAreaId?: string | null
          description?: string
        } | null
      }[]
    }
    setRascunho({
      id: existente.data.template.id,
      name: existente.data.template.name,
      description: existente.data.template.description,
      checklistName: payload.name ?? '',
      items:
        (payload.items ?? []).map((i) => ({
          text: i.text,
          criaItem: Boolean(i.child),
          workItemTypeId: i.child?.type?.id ?? null,
          workItemStatusId: i.child?.status?.id ?? null,
          workItemPriorityId: i.child?.priority?.id ?? null,
          companyAreaId: i.child?.companyAreaId ?? null,
          flowStepId: i.child?.flowStep?.id ?? null,
          description: i.child?.description ?? '',
        })) ?? [],
    })
    setCarregou(true)
  }, [existente.data, carregou, setRascunho])

  // mesmo rotulo traduzido das outras telas: chave i18n com o codigo de fallback
  const tipos = (bootstrap.data?.workItemTypes ?? [])
    .filter((x) => x.isActive)
    .map((x) => ({ value: x.id, label: rotuloTraduzido(x, t) }))
  const podeSalvar = rascunho.name.trim() !== '' && rascunho.checklistName.trim() !== ''

  /**
   * SEMPRE na forma funcional: aplicar a mudanca sobre o `rascunho` do closure
   * fazia a segunda edicao partir de um estado velho e DESFAZER a primeira
   * (escolher a prioridade e depois digitar a descricao perdia a prioridade —
   * visto em 2026-08-30).
   */
  const mexer = (fn: (r: Rascunho) => Rascunho) => setRascunho((r) => (r ? fn(r) : r))
  const trocarItem = (i: number, patch: Partial<ItemChecklistTemplate>) =>
    mexer((r) => ({ ...r, items: r.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }))

  if (rascunho.id && !carregou) return <LoadingState label="Carregando template…" />

  return (
    <FormScreen
      title={rascunho.id ? 'Editar template de checklist' : 'Novo template de checklist'}
      onClose={onFechar}
      dirty
      saving={salvar.isPending}
      saveDisabled={!podeSalvar}
      onSubmit={() =>
        salvar.mutate(
          {
            id: rascunho.id,
            name: rascunho.name.trim(),
            description: rascunho.description.trim(),
            checklist: {
              name: rascunho.checklistName.trim(),
              items: rascunho.items.filter((i) => i.text.trim() !== ''),
            },
          },
          {
            onSuccess: () => {
              toast.push({ tone: 'success', message: 'Template de checklist salvo.' })
              onFechar()
            },
          },
        )
      }
    >
      <FormSection title="Identificação">
        <FormRow cols="2fr 3fr">
          <FieldBlock label="Nome do template">
            {({ id }) => <Input id={id} value={rascunho.name} onChange={(e) => mexer((r) => ({ ...r, name: e.target.value }))} />}
          </FieldBlock>
          <FieldBlock label="Descrição">
            {({ id }) => (
              <Input id={id} value={rascunho.description} onChange={(e) => mexer((r) => ({ ...r, description: e.target.value }))} />
            )}
          </FieldBlock>
        </FormRow>
        <FormRow>
          <FieldBlock label="Título do checklist" hint="É o nome que a lista terá dentro do Item de Trabalho.">
            {({ id }) => (
              <Input
                id={id}
                value={rascunho.checklistName}
                placeholder="Ex.: Publicação"
                onChange={(e) => mexer((r) => ({ ...r, checklistName: e.target.value }))}
              />
            )}
          </FieldBlock>
        </FormRow>
      </FormSection>

      <FormSection title="Itens da lista">
        <p className="tpl__ajuda">
          Marque <strong>“Vira Item de Trabalho”</strong> quando a linha precisar virar um item filho de verdade — com tipo,
          etapa e responsável próprios. Sem marcar, é só uma linha de checar.
        </p>
        <div className="tpl__itens">
          {rascunho.items.map((item, i) => (
            <div key={i} className="tpl__linha">
            <div className="tpl__item">
              <i className="fas fa-grip-vertical tpl__item-grip" aria-hidden />
              <Input
                value={item.text}
                placeholder={`Item ${i + 1}`}
                aria-label={`Texto do item ${i + 1}`}
                onChange={(e) => trocarItem(i, { text: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    mexer((r) => ({ ...r, items: [...r.items, { text: '', criaItem: false }] }))
                  }
                }}
              />
              <label className="tpl__item-check">
                <input
                  type="checkbox"
                  checked={item.criaItem}
                  onChange={(e) => trocarItem(i, { criaItem: e.target.checked })}
                />
                Vira Item de Trabalho
              </label>
              <span className="tpl__item-vazio" aria-hidden />
              <button
                type="button"
                className="tpl__item-del"
                title="Remover"
                aria-label={`Remover item ${i + 1}`}
                onClick={() => mexer((r) => ({ ...r, items: r.items.filter((_, idx) => idx !== i) }))}
              >
                <i className="fas fa-times" aria-hidden />
              </button>
            </div>
            {/* Marcado, a linha vira Item de Trabalho — e leva os MESMOS campos
                do "Converter em item" (pedido 2026-08-30). */}
            {item.criaItem ? (
              <ConfigDoItemGerado
                indice={i}
                item={item}
                bootstrap={bootstrap.data}
                rotulo={(x) => rotuloTraduzido(x, t)}
                onMudar={(patch) => trocarItem(i, patch)}
              />
            ) : null}
            </div>
          ))}
        </div>
        <Button variant="ghost" onClick={() => mexer((r) => ({ ...r, items: [...r.items, { text: '', criaItem: false }] }))}>
          <i className="fas fa-plus" aria-hidden /> Adicionar item
        </Button>
      </FormSection>
    </FormScreen>
  )
}

/**
 * Configuração do Item de Trabalho que uma linha do checklist vai gerar
 * (2026-08-30). São os MESMOS campos do "Converter em item": tipo, status,
 * prioridade, área, etapa e descrição (opcional) — para a cadeia nascer pronta
 * ao aplicar o template.
 */
function ConfigDoItemGerado({
  indice,
  item,
  bootstrap,
  rotulo,
  onMudar,
}: {
  indice: number
  item: ItemChecklistTemplate
  bootstrap: Bootstrap | undefined
  rotulo: (row: { code: string; nameTranslationKey?: string | null }) => string
  onMudar: (patch: Partial<ItemChecklistTemplate>) => void
}) {
  const tipos = (bootstrap?.workItemTypes ?? []).filter((x) => x.isActive)
  const status = (bootstrap?.workItemStatuses ?? []).filter((s) => s.isActive)
  const prioridades = bootstrap?.priorities ?? []
  const areas = bootstrap?.areas ?? []
  // mesma regra do resto: só a PRIMEIRA etapa de cada fluxo do tipo
  const etapas = useEtapasIniciais(item.workItemTypeId, tipos)

  const campo = (chave: keyof ItemChecklistTemplate, valor: string) =>
    onMudar({ [chave]: valor || null } as Partial<ItemChecklistTemplate>)

  return (
    <div className="tpl__config" aria-label={`Configuração do item ${indice + 1}`}>
      <div className="tpl__config-grade">
        <label className="tpl__config-campo">
          <span>Tipo</span>
          <Select
            value={item.workItemTypeId ?? ''}
            options={[{ value: '', label: 'Tipo padrão' }, ...tipos.map((x) => ({ value: x.id, label: rotulo(x) }))]}
            // trocar o tipo muda os fluxos oferecidos: a etapa antiga não vale mais
            onChange={(v) => onMudar({ workItemTypeId: v || null, flowStepId: null })}
          />
        </label>
        <label className="tpl__config-campo">
          <span>Status</span>
          <Select
            value={item.workItemStatusId ?? ''}
            options={[{ value: '', label: 'Status inicial' }, ...status.map((s) => ({ value: s.id, label: rotulo(s) }))]}
            onChange={(v) => campo('workItemStatusId', v)}
          />
        </label>
        <label className="tpl__config-campo">
          <span>Prioridade</span>
          <Select
            value={item.workItemPriorityId ?? ''}
            options={[
              { value: '', label: 'Prioridade padrão' },
              ...prioridades.map((p) => ({ value: p.id, label: rotulo(p) })),
            ]}
            onChange={(v) => campo('workItemPriorityId', v)}
          />
        </label>
        <label className="tpl__config-campo">
          <span>Área</span>
          <Select
            value={item.companyAreaId ?? ''}
            options={[{ value: '', label: 'Sem área' }, ...areas.map((a) => ({ value: a.id, label: rotulo(a) }))]}
            onChange={(v) => campo('companyAreaId', v)}
          />
        </label>
        <label className="tpl__config-campo">
          <span>Etapa</span>
          {etapas.length > 0 ? (
            <Select
              value={item.flowStepId ?? ''}
              options={[
                { value: '', label: 'Etapa inicial do fluxo' },
                ...etapas.map((n) => ({ value: n.flowStepId, label: `${n.stepName} · ${n.flowName}` })),
              ]}
              onChange={(v) => campo('flowStepId', v)}
            />
          ) : (
            <Input value={item.workItemTypeId ? 'Tipo sem fluxo' : 'Escolha o tipo'} readOnly />
          )}
        </label>
      </div>
      <label className="tpl__config-campo">
        <span>Descrição (opcional)</span>
        <Textarea
          rows={2}
          value={item.description ?? ''}
          placeholder="O que precisa ser feito?"
          onChange={(e) => onMudar({ description: e.target.value })}
        />
      </label>
    </div>
  )
}
